import { useState } from 'react'
import { notifications } from '@mantine/notifications'
import {
  useAreaClienteCreate,
  useAreaClienteDelete,
  useAreaClienteUpdate,
  useAreasCliente,
  type AreaCliente,
} from '../../hooks/useEmpresas'
import { extractApiError } from '../../lib/extractApiError'

export default function AreasClienteSection({ empresaId }: { empresaId: string }) {
  const areas = useAreasCliente(empresaId)
  const criar = useAreaClienteCreate(empresaId)
  const atualizar = useAreaClienteUpdate(empresaId)
  const excluir = useAreaClienteDelete(empresaId)
  const [nome, setNome] = useState('')
  const [codigo, setCodigo] = useState('')
  const [responsavel, setResponsavel] = useState('')
  const [ramal, setRamal] = useState('')
  const [ocupantes, setOcupantes] = useState('')
  const [computadores, setComputadores] = useState('')
  const [metragem, setMetragem] = useState('')
  const [editandoId, setEditandoId] = useState<string | null>(null)

  function limparFormulario() {
    setNome(''); setCodigo(''); setResponsavel(''); setRamal('')
    setOcupantes(''); setComputadores(''); setMetragem(''); setEditandoId(null)
  }

  function carregarEdicao(area: AreaCliente) {
    setEditandoId(area.id); setNome(area.nome); setCodigo(area.codigo)
    setResponsavel(area.responsavel_nome); setRamal(area.ramal)
    setOcupantes(area.qtd_ocupantes?.toString() ?? '')
    setComputadores(area.qtd_computadores?.toString() ?? '')
    setMetragem(area.metragem_m2 ?? '')
  }

  async function adicionar() {
    if (!nome.trim()) return
    try {
      const data = {
        nome: nome.trim(), codigo: codigo.trim(), status: 'ATIVO',
        responsavel_nome: responsavel.trim(), ramal: ramal.trim(),
        qtd_ocupantes: ocupantes ? Number(ocupantes) : null,
        qtd_computadores: computadores ? Number(computadores) : null,
        metragem_m2: metragem || null,
      }
      const eraEdicao = Boolean(editandoId)
      if (editandoId) await atualizar.mutateAsync({ areaId: editandoId, data })
      else await criar.mutateAsync(data)
      limparFormulario()
      notifications.show({ color: 'green', message: eraEdicao ? 'Local atualizado.' : 'Local cadastrado.' })
    } catch (err) {
      notifications.show({ color: 'red', message: extractApiError(err, 'Não foi possível cadastrar o local.') })
    }
  }

  async function remover(id: string, nomeArea: string) {
    if (!confirm(`Remover o local "${nomeArea}"?`)) return
    try {
      await excluir.mutateAsync(id)
    } catch (err) {
      notifications.show({ color: 'red', message: extractApiError(err, 'Não foi possível remover o local.') })
    }
  }

  return (
    <section style={{ background: 'white', padding: '1.5rem', borderRadius: 8, marginBottom: '1rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>Locais / ambientes</h2>
      <p style={{ color: '#666', fontSize: 14 }}>
        Usados para agrupar equipamentos e planejar manutenções e coletas de qualidade do ar.
        A importação de equipamentos cria os locais automaticamente e evita duplicidade de nomes.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 16 }}>
        <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do local" aria-label="Nome do local" />
        <input value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Código (opcional)" aria-label="Código do local" />
        <input value={responsavel} onChange={(e) => setResponsavel(e.target.value)} placeholder="Responsável" />
        <input value={ramal} onChange={(e) => setRamal(e.target.value)} placeholder="Ramal" />
        <input type="number" min="0" value={ocupantes} onChange={(e) => setOcupantes(e.target.value)} placeholder="Ocupantes" />
        <input type="number" min="0" value={computadores} onChange={(e) => setComputadores(e.target.value)} placeholder="Computadores" />
        <input type="number" min="0" step="0.01" value={metragem} onChange={(e) => setMetragem(e.target.value)} placeholder="Área (m²)" />
        <button type="button" disabled={!nome.trim() || criar.isPending || atualizar.isPending} onClick={adicionar}>{editandoId ? 'Salvar alterações' : 'Adicionar local'}</button>
        {editandoId && <button type="button" onClick={limparFormulario}>Cancelar edição</button>}
      </div>
      {areas.isLoading ? <p>Carregando locais...</p> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={{ textAlign: 'left' }}>Local</th><th style={{ textAlign: 'left' }}>Detalhes</th><th>Equipamentos</th><th /></tr></thead>
          <tbody>{areas.data?.results.map((area) => (
            <tr key={area.id}>
              <td style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>{area.nome}</td>
              <td style={{ borderBottom: '1px solid #eee', fontSize: 13 }}>
                {area.codigo || 'Sem código'} · {area.metragem_m2 ? `${area.metragem_m2} m²` : 'área não informada'}<br />
                {area.qtd_ocupantes ?? '—'} ocupantes · {area.qtd_computadores ?? '—'} computadores
              </td>
              <td style={{ textAlign: 'center', borderBottom: '1px solid #eee' }}>{area.equipamentos_count}</td>
              <td style={{ textAlign: 'right', borderBottom: '1px solid #eee' }}>
                <button type="button" onClick={() => carregarEdicao(area)}>Editar</button>{' '}
                <button type="button" disabled={area.equipamentos_count > 0 || excluir.isPending} title={area.equipamentos_count > 0 ? 'Transfira ou inative os equipamentos antes de remover.' : ''} onClick={() => remover(area.id, area.nome)}>Remover</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </section>
  )
}
